import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import Modal from './Modal';
import { Upload, Download, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import './CsvImportModal.css';

function CsvImportModal({
  isOpen, onClose, onImport, entityName,
  sampleColumns, sampleData, requiredColumns,
  matchDescription, onLoadData
}) {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [parseError, setParseError] = useState('');
  const [updateExisting, setUpdateExisting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setParsedData([]);
    setHeaders([]);
    setParseError('');
    setUpdateExisting(false);
    setImporting(false);
    setResult(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setParseError('');
    setResult(null);

    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          setParseError('CSV parsing error: ' + results.errors[0].message);
          return;
        }
        if (results.data.length === 0) {
          setParseError('CSV file is empty');
          return;
        }
        setHeaders(results.meta.fields || []);
        setParsedData(results.data);
      },
      error: (err) => {
        setParseError('Failed to read file: ' + err.message);
      }
    });
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const response = await onImport(parsedData, updateExisting);
      setResult(response.data);
      onLoadData();
    } catch (err) {
      setResult({
        error: err.response?.data?.error || 'Import failed'
      });
    } finally {
      setImporting(false);
    }
  };

  const downloadSample = () => {
    const csvContent = [
      sampleColumns.join(','),
      ...sampleData.map(row =>
        sampleColumns.map(col => {
          const val = row[col] != null ? String(row[col]) : '';
          return val.includes(',') || val.includes('"')
            ? `"${val.replace(/"/g, '""')}"`
            : val;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${entityName.toLowerCase()}-import-template.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const previewRows = parsedData.slice(0, 5);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`Import ${entityName}`} size="lg">
      <div className="modal-body">
        {/* Results view */}
        {result ? (
          <div className="csv-import-results">
            {result.error ? (
              <div className="alert alert-error">{result.error}</div>
            ) : (
              <>
                <h3 style={{ marginBottom: 16 }}>Import Complete</h3>
                <div className="result-stat">
                  <CheckCircle size={18} style={{ color: 'var(--color-success)' }} />
                  <span>Created: <strong>{result.created}</strong></span>
                </div>
                {result.updated > 0 && (
                  <div className="result-stat">
                    <CheckCircle size={18} style={{ color: 'var(--accent-primary)' }} />
                    <span>Updated: <strong>{result.updated}</strong></span>
                  </div>
                )}
                {result.skipped > 0 && (
                  <>
                    <div className="result-stat">
                      <AlertCircle size={18} style={{ color: 'var(--color-warning, #f59e0b)' }} />
                      <span>Skipped: <strong>{result.skipped}</strong></span>
                    </div>
                    {result.skippedRows && result.skippedRows.length > 0 && (
                      <div className="skipped-list">
                        {result.skippedRows.map((item, i) => (
                          <div key={i} className="skipped-item">
                            <strong>Row {item.row}:</strong> {item.reason}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        ) : parsedData.length > 0 ? (
          /* Preview view */
          <>
            <div className="csv-import-file-info">
              <FileText size={18} />
              <span>{file?.name}</span>
              <span style={{ color: 'var(--text-tertiary)' }}>
                ({parsedData.length} rows detected)
              </span>
            </div>

            <div className="csv-import-preview">
              <table className="table" aria-label="CSV preview">
                <thead>
                  <tr>
                    {headers.map(h => (
                      <th scope="col" key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, i) => (
                    <tr key={i}>
                      {headers.map(h => (
                        <td key={h}>{row[h] || ''}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {parsedData.length > 5 && (
              <p style={{ fontSize: 13, color: 'var(--text-tertiary)', marginTop: 4 }}>
                Showing first 5 of {parsedData.length} rows
              </p>
            )}

            <div style={{ marginTop: 16 }}>
              <label className="checkbox-wrapper">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={updateExisting}
                  onChange={(e) => setUpdateExisting(e.target.checked)}
                />
                Update existing {entityName.toLowerCase()} (match by {matchDescription})
              </label>
            </div>
          </>
        ) : (
          /* File selection view */
          <>
            <div style={{ marginBottom: 16 }}>
              <button className="btn btn-secondary btn-sm" onClick={downloadSample}>
                <Download size={16} />
                Download Sample CSV
              </button>
            </div>

            <div
              className="csv-import-dropzone"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                aria-label={`Upload CSV file to import ${entityName}`}
              />
              <div className="dropzone-icon">
                <Upload size={36} />
              </div>
              <div className="dropzone-text">
                Click to browse for a CSV file
              </div>
            </div>

            {parseError && (
              <div className="alert alert-error" style={{ marginTop: 12 }}>
                {parseError}
              </div>
            )}

            <div className="csv-import-columns">
              <strong>Required columns:</strong> {requiredColumns.join(', ')}
              <br />
              <strong>Optional columns:</strong>{' '}
              {sampleColumns
                .filter(c => !requiredColumns.includes(c))
                .join(', ')}
            </div>
          </>
        )}
      </div>
      <div className="modal-footer">
        {result ? (
          <button className="btn btn-primary" onClick={handleClose}>
            Done
          </button>
        ) : parsedData.length > 0 ? (
          <>
            <button className="btn btn-secondary" onClick={handleClose}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleImport}
              disabled={importing}
            >
              {importing ? 'Importing...' : `Import ${parsedData.length} Records`}
            </button>
          </>
        ) : (
          <button className="btn btn-secondary" onClick={handleClose}>
            Cancel
          </button>
        )}
      </div>
    </Modal>
  );
}

export default CsvImportModal;
